import React, { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { AppDispatch } from "newui/store";
import { ReactComponent as ShareIcon } from "icons/share-icon.svg";
import { createRFQTOTAL } from "actions/supplierflow/supplierRfqActions";
import { useCurrentCompany, useCurrentUser } from "hooks/auth";
import { useProfileContext } from "newui/features/Auth/context/ProfileContext";
import { useUpdateUserTourFlags } from "newui/features/Auth/hooks/useUpdateUserTourFlags";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";
import { useNavigationPaths } from "newui/features/Auth/hooks/useNavigationPaths";
import { rfxSubmission } from "newui/features/Rfx/Submission";
import { ShareProfileModal } from "components/ShareProfile/ShareProfileModal";
import { ExecutiveSwitchButton } from "newui/features/ExecutiveView/components/ExecutiveSwitchButton";
import { AppMenuHeader } from "./AppMenuHeader";
import { AppMenuItem } from "./AppMenuItem/AppMenuItem";
import { AppMenuFooter } from "./AppMenuFooter";
import { AppMenuCommonBottomItems } from "./AppMenuCommonBottomItems";
import { AppMenuItemRfxMenu } from "./AppMenuItem/AppMenuItemRfxMenu";
import { useModuleCan } from "newui/features/RolesPermissions/hooks/useModuleCan";

interface AppMenuSupplierProps {
  isCollapsed: boolean;
  supplier_rfq_total?: number;
  onNavigate?: () => void;
}

const AppMenuSupplierComponent: React.FC<AppMenuSupplierProps> = ({
  isCollapsed,
  supplier_rfq_total,
  onNavigate,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const { canView: canShareProfile } = useModuleCan("share_profile");
  const [displaySideMenu, setDisplaySideMenu] = useState(false);
  const [showSupplierMenu, setShowSupplierMenu] = useState<"init" | "yes" | "no">("init");
  const { setForceRestartTour } = useProfileContext();
  const currentCompany = useCurrentCompany();
  const { isSuperAdmin } = useCurrentUser();
  const { logoutUser } = useAuthContext();
  const updateUserTourFlags = useUpdateUserTourFlags();
  const { supplierPaths } = useNavigationPaths();

  useEffect(() => {
    if (!currentCompany) {
      return;
    }
    if (
      isSuperAdmin ||
      currentCompany.grouping === "procurer" ||
      currentCompany.grouping === "bidder_procurer"
    ) {
      setShowSupplierMenu("yes");
    } else {
      setShowSupplierMenu("no");
    }
  }, [currentCompany, isSuperAdmin]);

  useEffect(() => {
    if (showSupplierMenu !== "init") {
      setDisplaySideMenu(true);
    }
  }, [showSupplierMenu]);

  useEffect(() => {
    const abortController = new AbortController();
    const fetchData = async () => {
      try {
        const response = await rfxSubmission.api.getAllSubmissions(1, "", 12);
        const responseData = response.data.data;
        const totalRFQs = responseData.all.meta.total;
        await dispatch(createRFQTOTAL(totalRFQs));
      } catch (_e) {
        // ignore
      }
    };
    fetchData();
    return () => {
      abortController.abort();
    };
  }, [dispatch]);

  const handleResetTourFlags = async () => {
    await updateUserTourFlags(false, false);
    setForceRestartTour(true);
  };

  if (!supplierPaths) {
    return null;
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <AppMenuHeader isCollapsed={isCollapsed} />
      <ExecutiveSwitchButton isCollapsed={isCollapsed} />

      {/* Top nav — scrollable */}
      <div className={`flex-1 overflow-y-auto py-1 ${isCollapsed ? "overflow-x-hidden" : ""}`}>
        {displaySideMenu && (
          <AppMenuItemRfxMenu
            isCollapsed={isCollapsed}
            links={{
              tendersQuotes: supplierPaths.rfx.tendersQuotes,
              approvals: supplierPaths.rfx.approvals,
              panel: supplierPaths.rfx.panel,
            }}
            total={supplier_rfq_total ?? 0}
            onNavigate={onNavigate}
          />
        )}
      </div>

      {/* Bottom nav — pinned to bottom, ordered top→bottom: Preview Profile → Company Profile → My Profile → Settings → Share Profile → Footer */}
      {displaySideMenu && (
        <div className="mt-auto shrink-0">
          <AppMenuCommonBottomItems
            paths={supplierPaths}
            isCollapsed={isCollapsed}
            exactProfileLinks
            onNavigate={onNavigate}
          />
          {/*
            * PRO-712: Share Profile was rendered for every supplier-menu viewer.
            * The permission matrix already scopes it precisely —
            * `share_profile` is Full for Bidder System Administrator and Bidder
            * Lead and `none` for the other eleven roles — it was simply never
            * consulted.
            *
            * Reading the capability rather than the company's account type is
            * also what makes this correct in HYBRID mode: standing is a property
            * of the user's role, not of which profile they are currently viewing,
            * so a hybrid company's Bidder Support does not acquire the button by
            * switching profile.
            */}
          {canShareProfile && (
            <AppMenuItem
              name="Share Profile"
              icon={<ShareIcon />}
              type="button"
              onClick={() => setIsOpenModal(true)}
              isCollapsed={isCollapsed}
              testId="share-profile"
              tourName="ShareProfile"
            />
          )}
          <AppMenuFooter
            isCollapsed={isCollapsed}
            onLogout={logoutUser}
            onHelpClick={handleResetTourFlags}
            showHelp
          />
        </div>
      )}

      {canShareProfile && (
        <ShareProfileModal
          isOpen={isOpenModal}
          setIsOpen={setIsOpenModal}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  user: state.auth.user,
  supplier_rfq_total: state?.supplier_rfq?.total,
});

export const AppMenuSupplier = connect(
  mapStateToProps,
  {}
)(AppMenuSupplierComponent);
